/* /gov/integrations — Saudi government services: what each offers, how StartHR uses it, how it can be accessed,
 * and an honest connection status (nothing is connected yet). Data: contracts/gov-apis.json (+ apis.md). */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Alert, Button, Card, Chip, EmptyState, Icon, Kpi, PageHeader, SearchBox, Seg } from "../../ui";
import { copyFor, GOV_APIS, PHASE_AR, PHASE_ITEMS_AR, STATUS_META, type GovService } from "./components/govApis";
import { shortUrl } from "./components/renewals";
import { useCountry } from "../../lib/country";
import GovCountry from "./components/GovCountry";
import "./core.css";

type Filter = "all" | "available" | "partner-only" | "no public API" | "bank/PSP-only";

function Ext({ href, children, className, testId }: { href: string; children: React.ReactNode; className?: string; testId?: string }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>{children}</a>;
}

function ServiceCard({ s }: { s: GovService }) {
  const { L, isAr } = useI18n();
  const c = copyFor(s);
  const meta = STATUS_META[s.status] ?? { tone: "", en: s.status, ar: s.status };
  const pick = (b: [string, string]) => (isAr ? b[1] : b[0]);
  const noApi = s.status === "no public API";
  return (
    <section className="card gi-card" data-testid={"svc-" + c.id} data-status={s.status}>
      <div className="row between nw" style={{ alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h3>{pick(c.short)}</h3>
          <p className="xs muted gi-op">{s.operator}</p>
        </div>
        <Chip tone={meta.tone} testId={"svc-status-" + c.id}>{L(meta.en, meta.ar)}</Chip>
      </div>
      <div className="gi-conn" data-testid={"svc-conn-" + c.id}>
        <Icon name={noApi ? "x" : "wifi-off"} className="s" />
        <span><b>{L("Connection status", "حالة الربط")}:</b> {pick(c.connection)}</span>
      </div>
      <div><p className="gi-label">{L("What it offers", "ما الذي تقدمه")}</p><p className="sm" style={{ margin: 0 }}>{pick(c.offers)}</p></div>
      <div><p className="gi-label">{L("How StartHR uses it", "كيف يستخدمها ستارت HR")}</p><p className="sm" style={{ margin: 0 }}>{pick(c.rukn)}</p></div>
      <div><p className="gi-label">{L("Access model", "طريقة الوصول")}</p><p className="sm" style={{ margin: 0 }}>{pick(c.access)}</p></div>
      {(noApi || c.alternative) && (
        <div className="gi-alt" data-testid={"svc-alt-" + c.id}>
          {noApi && <b>{L("There is no API for this service. ", "لا توجد واجهة برمجية لهذه الخدمة. ")}</b>}
          {c.alternative ? pick(c.alternative) : s.practicalAlternative}
        </div>
      )}
      <details className="accordion">
        <summary className="sm">{L("Details from our research", "تفاصيل من بحثنا")}{isAr && <span className="xs muted"> · {L("", "المصدر بالإنجليزية")}</span>}</summary>
        <div className="stack" style={{ gap: 10 }}>
          <div><p className="gi-label">{L("Offers", "الخدمات")}</p><ul className="core-list">{s.whatItOffers.map((x) => <li key={x}>{x}</li>)}</ul></div>
          <div><p className="gi-label">{L("How to apply", "طريقة التقديم")}</p><ol className="core-list">{s.howToApply.map((x) => <li key={x}>{x}</li>)}</ol></div>
          <div><p className="gi-label">{L("Cost", "التكلفة")}</p><p className="sm" style={{ margin: 0 }}>{s.cost}</p></div>
          <div><p className="gi-label">{L("Authentication", "المصادقة")}</p><p className="sm" style={{ margin: 0 }}>{s.authMethod}{(s.authVerify || s.verify) && <> <Chip tone="warn" nodot>{L("Verify", "تحتاج تحققًا")}</Chip></>}</p></div>
          {s.practicalAlternative && <div><p className="gi-label">{L("Practical alternative", "البديل العملي")}</p><p className="sm" style={{ margin: 0 }}>{s.practicalAlternative}</p></div>}
          <div><p className="gi-label">{L("Sources", "المصادر")}</p><ul className="core-list">{s.sources.map((u) => <li key={u}><Ext href={u}><span dir="ltr">{shortUrl(u)}</span></Ext></li>)}</ul></div>
        </div>
      </details>
      <div className="gi-links">
        <Ext href={c.official} className="btn sm" testId={"svc-official-" + c.id}><Icon name="external" /><span>{L("Official site", "الموقع الرسمي")}</span></Ext>
        {s.docsUrl && s.docsUrl !== c.official && <Ext href={s.docsUrl} className="btn ghost sm" testId={"svc-docs-" + c.id}><Icon name="book" /><span>{L("Docs / plan", "الوثائق / الباقة")}</span></Ext>}
      </div>
    </section>
  );
}

/** Saudi Arabia: Muqeem / Qiwa fees and services. */
function SaudiGovIntegrations() {
  const { L, t, lang, isAr } = useI18n();
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const services = GOV_APIS.services;
  const needle = q.trim().toLowerCase();
  const shown = services.filter((s) => {
    if (filter !== "all" && s.status !== filter) return false;
    if (!needle) return true;
    const c = copyFor(s);
    return [s.name, s.operator, c.short[0], c.short[1], ...s.relevantFeatures].join(" ").toLowerCase().includes(needle);
  });
  const count = (st: string) => services.filter((s) => s.status === st).length;

  return (
    <div data-testid="gov-integrations">
      <PageHeader
        back={{ to: "/gov", label: L("Government services", "الخدمات الحكومية") }}
        title={L("Government integrations", "التكاملات الحكومية")}
        sub={L("Which Saudi government platforms StartHR can connect to, how, and what we do where no API exists.", "المنصات الحكومية السعودية التي يمكن لستارت HR الربط معها، وطريقة ذلك، وما نفعله حين لا تتوفر واجهة برمجية.")}
        actions={<Button icon="book" onClick={() => nav("/gov")} data-testid="btn-guides">{L("Renewal guides", "أدلة التجديد")}</Button>}
      />
      <Alert tone="warn" tint testId="not-connected"
        title={L("Connection status: Not connected", "حالة الربط: غير متصل")}
        desc={L("StartHR is not connected to any government platform yet. Each connection needs an agreement with the operator (for example Elm for Muqeem, Masdr for GOSI, Mudad for payroll). Until then we use guided steps, deep links and file exports — never screen-scraping.", "ستارت HR غير متصل بأي منصة حكومية حتى الآن. يتطلب كل ربط اتفاقية مع الجهة المشغلة (مثل عِلم لمقيم، ومصدر للتأمينات، ومدد للرواتب). وحتى ذلك الحين نعتمد الخطوات الموجّهة والروابط المباشرة وتصدير الملفات، دون أي استخراج آلي من البوابات.")} />
      <div className="g g-4 mb mt" data-testid="gi-kpis">
        <Kpi testId="kpi-available" label={L("Available", "متاحة")} value={String(count("available"))} icon="check" foot={L("self-serve or subscription", "ذاتية الخدمة أو بالاشتراك")} />
        <Kpi testId="kpi-partner" label={L("Partner-only", "للشركاء فقط")} value={String(count("partner-only") + count("bank/PSP-only"))} icon="users" tone="warn" foot={L("needs an agreement", "تحتاج اتفاقية")} />
        <Kpi testId="kpi-noapi" label={L("No public API", "لا توجد واجهة عامة")} value={String(count("no public API"))} icon="x" tone="bad" foot={L("guided manual workflow", "مسار يدوي موجّه")} />
        <Kpi testId="kpi-connected" label={L("Connected", "متصلة")} value="0" icon="wifi-off" tone="info" foot={L("of " + services.length + " services", "من " + services.length + " خدمة")} />
      </div>
      <section className="card mb">
        <div className="toolbar">
          <SearchBox value={q} onChange={setQ} placeholder={L("Search services…", "ابحث في الخدمات…")} testId="gi-search" />
          <Seg<Filter> label={L("Access", "الوصول")} value={filter} onChange={setFilter} items={[["all", t("All")], ["available", L("Available", "متاحة")], ["partner-only", L("Partner-only", "للشركاء")], ["no public API", L("No API", "بلا واجهة")], ["bank/PSP-only", L("Bank / PSP", "بنوك")]]} />
        </div>
      </section>
      {shown.length ? <div className="gi-grid mb" data-testid="gi-grid">{shown.map((s) => <ServiceCard key={s.name} s={s} />)}</div>
        : <EmptyState title={L("No services match", "لا توجد خدمات مطابقة")} desc={L("Clear the search or pick another filter.", "امسح البحث أو اختر تصفية أخرى.")} action={<Button variant="sm" onClick={() => { setQ(""); setFilter("all"); }}>{t("Clear filters")}</Button>} />}
      <Card title={L("Integration roadmap", "خارطة طريق التكامل")} sub={L("Researched " + gDate(GOV_APIS.researchedOn, lang), "تاريخ البحث " + gDate(GOV_APIS.researchedOn, lang))} testId="roadmap">
        <div className="stack lg">
          {GOV_APIS.roadmap.map((p, k) => (
            <div key={p.phase} className="gi-phase" data-testid={"phase-" + k}>
              <span className="gi-dot" aria-hidden="true" />
              <h4 style={{ margin: "0 0 6px", fontSize: 14.5 }}>{isAr ? PHASE_AR[k] ?? p.phase : p.phase}</h4>
              <ul className="core-list">{p.items.map((x, j) => <li key={j}>{isAr ? PHASE_ITEMS_AR[k]?.[j] ?? x : x}</li>)}</ul>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function GovIntegrations() {
  const c = useCountry();
  return c.code === "SA" ? <SaudiGovIntegrations /> : <GovCountry c={c} testId="gov-integrations-country" />;
}
